import { useEffect, useRef } from 'react';
import { useUserStore } from '@/stores/user/userStore';
import useAnnouncementModalStore from '@/stores/announcementModal/announcementModalStore';

const TWENTY_FOUR_HOURS_MS = 5 * 60 * 1000; // 5 min for testing (prod: 24 * 60 * 60 * 1000)

/**
 * Hook to manage the Announcement Modal.
 * Modal is shown 24h after the user last logged in (or first authenticated load), only if the backend says they haven't seen it.
 */
const useAnnouncementModal = () => {
  const user = useUserStore((state) => state.user);
  const auth = useUserStore((state) => state.auth);
  const clearJustLoggedIn = useUserStore((state) => state.clearJustLoggedIn);
  const store = useAnnouncementModalStore();
  const { checkAndShowModal, reset, setLoginTimestamp, loginTimestamp, _hasRehydrated } = store;
  const loggedNoTimestampRef = useRef(false);
  const loggedJustLoggedInRef = useRef(false);

  useEffect(() => {
    if (!user || !auth?.access_token) {
      console.log('[AnnouncementModal] No user/token - reset');
      reset();
      loggedNoTimestampRef.current = false;
      loggedJustLoggedInRef.current = false;
      return;
    }

    // When user just logged in: record timestamp and do not show modal yet
    if (auth?.just_logged_in) {
      const now = Date.now();
      if (!loggedJustLoggedInRef.current) {
        loggedJustLoggedInRef.current = true;
      }
      setLoginTimestamp(now);
      clearJustLoggedIn();
      loggedNoTimestampRef.current = false;
      return;
    }

    // Wait for persist rehydration before treating "no loginTimestamp" as final
    if (!_hasRehydrated) return;

    // Once rehydrated, if we still have no timestamp (e.g. existing session, never set), set it now
    if (!loginTimestamp) {
      if (!loggedNoTimestampRef.current) {
        loggedNoTimestampRef.current = true;
      }
      setLoginTimestamp(Date.now());
      return;
    }

    const elapsed = Date.now() - loginTimestamp;
    if (elapsed >= TWENTY_FOUR_HOURS_MS) {
      checkAndShowModal();
      return;
    }

    const remainingMs = TWENTY_FOUR_HOURS_MS - elapsed;
    const timeoutId = setTimeout(() => {
      checkAndShowModal();
    }, remainingMs);

    return () => clearTimeout(timeoutId);
  }, [user, auth?.access_token, auth?.just_logged_in, loginTimestamp, _hasRehydrated, checkAndShowModal, reset, setLoginTimestamp, clearJustLoggedIn]);

  return store;
};

export default useAnnouncementModal;
